'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useIsAuthenticated } from '@shared/hooks/useIsAuthenticated';
import { claimCouponsMutationOptions } from '@shared/mutations/claimCouponsMutationOptions';

/** 쿠폰 다운로드 방식 — 목록에서 고른 id 또는 사용자가 입력한 코드. */
export type ClaimCouponsVariables =
  | { couponIds: number[]; code?: never }
  | { code: string; couponIds?: never };

/** 발급 결과가 보유·다운로드가능 카운트에 즉시 반영돼야 하는 쿼리들. */
const COUPON_QUERY_PATHS = [
  '/api/v1/coupons',
  '/api/v1/coupons/claimable',
  '/api/v1/coupons/products/{productId}',
];

type UseClaimCouponsOptions = {
  /** 발급 성공. `ids` 는 실제로 발급된 쿠폰 id. */
  onSuccess?: (ids: number[], variables: ClaimCouponsVariables) => void;
  /**
   * 발급 실패. 로그인이 필요해 로그인 화면으로 보낸 경우는 실패가 아니므로 호출되지 않는다.
   */
  onError?: (error: unknown) => void;
};

/**
 * 쿠폰 다운로드 — 로그인 확인, 쿠폰 쿼리 무효화까지 처리하고 결과만 콜백으로 넘긴다.
 *
 * 로그인·react-query 관리는 이 훅이 맡는다.
 * 호출부는 안내 문구나 화면 갱신 같은 화면별 처리만 콜백으로 등록하면 된다.
 *
 * 비로그인 상태에서 호출하면 요청을 보내지 않고 로그인 화면으로 이동하며,
 * 로그인을 마치면 호출한 화면으로 그대로 돌아온다. 이때 발급은 자동으로 이어지지 않으므로
 * 사용자가 다시 눌러야 한다.
 *
 * @example
 * const claimCoupons = useClaimCoupons({
 *   onSuccess: (ids) => Toast.success(`쿠폰 ${ids.length}장을 받았어요`),
 *   onError: () => Toast.error('쿠폰을 받지 못했어요'),
 * });
 *
 * claimCoupons.mutate({ couponIds: [1, 2] });
 * claimCoupons.mutate({ code: 'WELCOME' });
 */
export function useClaimCoupons({
  onSuccess,
  onError,
}: UseClaimCouponsOptions = {}) {
  const isAuthenticated = useIsAuthenticated();
  const queryClient = useQueryClient();
  const claimCoupons = useMutation(claimCouponsMutationOptions());

  return useMutation({
    mutationFn: async (variables: ClaimCouponsVariables) => {
      if (!isAuthenticated) {
        redirectToSignIn();
        return null;
      }
      return claimCoupons.mutateAsync({ body: variables });
    },
    onSuccess: (data, variables) => {
      // 로그인 화면으로 보낸 경우 — 발급을 시도하지 않았으므로 후처리도 없다.
      if (!data) return;

      COUPON_QUERY_PATHS.forEach((path) => {
        void queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[1] === path,
        });
      });

      onSuccess?.(data.ids, variables);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
}

/** 로그인 후 호출한 화면(쿼리스트링 포함)으로 복귀시킨다. */
function redirectToSignIn() {
  if (typeof window === 'undefined') return;
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.href = `/api/auth/sign-in?${new URLSearchParams({ returnTo }).toString()}`;
}
